import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  NumberFieldProps,
  SelectFieldProps,
  SelectOption,
  TextareaFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import {
  ParamGeneralFieldRepository,
  systemSubTypes,
  systemTypeOptions,
} from '@/store/dac-general-params-state/param-general-field-state/param-general-field.repository';
import { ParamGeneralStatus, ParamGeneralType } from '@/types';
import { map } from 'rxjs';

interface GetParamGeneralFieldFormObjectProps {
  store: ParamGeneralFieldRepository;
}

export const getParamGeneralFieldFormObject = ({ store }: GetParamGeneralFieldFormObjectProps): DynamicForm => {
  // Observable for source field options based on selected source
  const currentSystemSubTypeOptions$ = store.getObservable<SelectOption>('sourceSelected').pipe(
    map(source => {
      if (!source?.code) return [];
      const key = source.code as string;
      return (systemSubTypes[key] || []).map(st => ({ name: st.label, code: st.value }));
    })
  );

  // Observable to check if type is SYSTEM
  const isNotSystemType$ = store.getObservable<SelectOption>('typeSelected').pipe(map(type => type?.code !== ParamGeneralType.SYSTEM));

  const nameField: DynamicField<TextFieldProps> = {
    label: 'dacGeneralParams.field.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralName'),
    props: {
      value: store.getNestedObservable<string>('dto', 'paramGeneralName'),
      onChange: (value: string) => {
        store.setNested('dto', 'paramGeneralName', value);
        store.clearError('paramGeneralName');
      },
      placeholder: 'dacGeneralParams.field.namePlaceholder',
    },
  };

  const labelField: DynamicField<TextFieldProps> = {
    label: 'dacGeneralParams.field.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralLabel'),
    props: {
      value: store.getNestedObservable<string>('dto', 'paramGeneralLabel'),
      onChange: (value: string) => {
        store.setNested('dto', 'paramGeneralLabel', value);
        store.clearError('paramGeneralLabel');
      },
      placeholder: 'dacGeneralParams.field.labelPlaceholder',
    },
  };

  const descriptionField: DynamicField<TextareaFieldProps> = {
    label: 'dacGeneralParams.field.description',
    variant: FieldVariant.TEXTAREA,
    error: store.getNestedObservable<string>('errors', 'paramGeneralDescription'),
    props: {
      value: store.getNestedObservable<string>('dto', 'paramGeneralDescription'),
      onChange: (value: string) => {
        store.setNested('dto', 'paramGeneralDescription', value);
        store.clearError('paramGeneralDescription');
      },
      placeholder: 'dacGeneralParams.field.descriptionPlaceholder',
      rows: 5,
      resize: 'none',
    },
  };

  const typeField: DynamicField<SelectFieldProps> = {
    label: 'dacGeneralParams.field.type',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralType'),
    props: {
      value: store.getObservable('typeSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('typeSelected', value);
        store.setNested('dto', 'paramGeneralType', value?.code);
        store.clearError('paramGeneralType');
        // Reset source fields when type changes
        if (value?.code !== ParamGeneralType.SYSTEM) {
          store.set('sourceSelected', { name: '', code: undefined });
          store.set('sourceFieldSelected', { name: '', code: undefined });
          store.setNested('dto', 'paramGeneralSource', '');
          store.setNested('dto', 'paramGeneralSourceField', '');
        }
      },
      placeholder: 'dacGeneralParams.field.typePlaceholder',
      options: Object.values(ParamGeneralType).map(type => ({
        code: type,
        name: `dacGeneralParams.types.${type}`,
      })),
    },
  };

  const orderField: DynamicField<NumberFieldProps> = {
    label: 'dacGeneralParams.field.order',
    variant: FieldVariant.NUMBER,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralOrder'),
    props: {
      value: store.getNestedObservable<number>('dto', 'paramGeneralOrder'),
      onChange: (value: number) => {
        store.setNested('dto', 'paramGeneralOrder', value);
        store.clearError('paramGeneralOrder');
      },
      placeholder: 'dacGeneralParams.field.orderPlaceholder',
    },
  };

  const statusField: DynamicField<SelectFieldProps> = {
    label: 'dacGeneralParams.field.status',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralStatus'),
    props: {
      value: store.getObservable('statusSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('statusSelected', value);
        store.setNested('dto', 'paramGeneralStatus', value?.code);
        store.clearError('paramGeneralStatus');
      },
      placeholder: 'dacGeneralParams.field.statusPlaceholder',
      options: [
        { name: 'Actif', code: ParamGeneralStatus.ACTIF },
        { name: 'Inactif', code: ParamGeneralStatus.NON_ACTIF },
      ],
    },
  };

  const sourceField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.nameSource',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isHidden: isNotSystemType$,
    error: store.getNestedObservable<string>('errors', 'paramGeneralSource'),
    props: {
      placeholder: 'dynamicDocument.form.placeholders.nameSource',
      value: store.getObservable<SelectOption>('sourceSelected'),
      onSelectChange: (option: SelectOption) => {
        store.set('sourceSelected', option);
        store.setNested('dto', 'paramGeneralSource', option?.code || '');
        // Reset source field when source changes
        store.set('sourceFieldSelected', { name: '', code: undefined });
        store.setNested('dto', 'paramGeneralSourceField', '');
        store.clearError('paramGeneralSource');
      },
      options: systemTypeOptions,
    },
  };

  const sourceFieldField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.fields',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isHidden: isNotSystemType$,
    error: store.getNestedObservable<string>('errors', 'paramGeneralSourceField'),
    props: {
      placeholder: 'dynamicDocument.form.placeholders.sourceField',
      value: store.getObservable<SelectOption>('sourceFieldSelected'),
      onSelectChange: (option: SelectOption) => {
        store.set('sourceFieldSelected', option);
        store.setNested('dto', 'paramGeneralSourceField', option?.code || '');
        store.clearError('paramGeneralSourceField');
      },
      options: currentSystemSubTypeOptions$,
    },
  };

  return {
    title: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [nameField, labelField] },
          { fields: [typeField, orderField] },
          { fields: [statusField] },
          { fields: [sourceField, sourceFieldField] },
          { fields: [descriptionField] },
        ],
      },
    ],
  };
};
